import type { ReactNode } from 'react';
import { Link } from 'wouter';

/**
 * Página de vendas (institucional/marketing) da AMA Capital para
 * incorporadoras. Serve o domínio raiz para visitantes deslogados (ver
 * App.tsx). Single-page: os links do menu são âncoras para seções desta
 * mesma página, não rotas separadas.
 */

const NAV_LINKS = [
  { href: '#o-que-muda', label: 'O que muda' },
  { href: '#como-funciona', label: 'Como funciona' },
  { href: '#precos', label: 'Preços' },
];

function IconeCheck() {
  return (
    <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true" className="shrink-0">
      <path d="M5 12.5l4.5 4.5L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconeCadeado() {
  return (
    <span
      className="flex h-10 w-10 items-center justify-center rounded-full"
      style={{ background: 'var(--accent-soft)' }}
    >
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
        <rect x="5" y="11" width="14" height="9" rx="2" stroke="var(--accent)" strokeWidth="1.7" />
        <path d="M8 11V7a4 4 0 018 0v4" stroke="var(--accent)" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    </span>
  );
}

function IconeMensagem() {
  return (
    <span
      className="flex h-10 w-10 items-center justify-center rounded-full"
      style={{ background: 'var(--accent-soft)' }}
    >
      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
        <path d="M4 5h16v11H8l-4 4V5z" stroke="var(--accent)" strokeWidth="1.7" strokeLinejoin="round" />
      </svg>
    </span>
  );
}

/** Fotografia da landing (imóvel/comprador/obra) com o mesmo enquadramento dos
 * antigos blocos decorativos: cantos arredondados, borda sutil e recorte
 * object-cover. Os arquivos ficam em apps/web/public/fotos (WebP otimizado). O
 * hero usa priority (acima da dobra); as demais carregam lazy. */
function Foto({
  src,
  alt,
  className,
  priority,
}: {
  src: string;
  alt: string;
  className?: string;
  priority?: boolean;
}) {
  return (
    <div className={`relative overflow-hidden rounded-[18px] border border-line ${className ?? ''}`}>
      <img
        src={src}
        alt={alt}
        loading={priority ? 'eager' : 'lazy'}
        fetchPriority={priority ? 'high' : 'auto'}
        className="h-full w-full object-cover"
      />
    </div>
  );
}

function Numero({ n }: { n: number }) {
  return (
    <span
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white"
      style={{ background: 'var(--accent)' }}
    >
      {n}
    </span>
  );
}

function Pilar({
  n,
  titulo,
  texto,
  selo,
  foto,
}: {
  n: number;
  titulo: string;
  texto: string;
  selo: string;
  foto: { src: string; alt: string };
}) {
  return (
    <div className="grid items-center gap-8 md:grid-cols-2">
      <Foto src={foto.src} alt={foto.alt} className={`h-56 md:h-72 ${n % 2 === 0 ? 'md:order-2' : ''}`} />
      <div className={n % 2 === 0 ? 'md:order-1' : ''}>
        <Numero n={n} />
        <h3 className="mt-4 font-serif text-2xl font-medium tracking-tight text-ink">{titulo}</h3>
        <p className="mt-3 text-[17px] leading-relaxed text-ink-2">{texto}</p>
        <div
          className="mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium text-accent"
          style={{ background: 'var(--accent-soft)' }}
        >
          <IconeCheck /> {selo}
        </div>
      </div>
    </div>
  );
}

function Passo({ n, titulo, texto }: { n: number; titulo: string; texto: string }) {
  return (
    <div className="card p-6">
      <Numero n={n} />
      <h3 className="mt-4 text-[17px] font-semibold text-ink">{titulo}</h3>
      <p className="mt-2 text-base leading-relaxed text-ink-3">{texto}</p>
    </div>
  );
}

function StatBlock({ valor, texto }: { valor: string; texto: string }) {
  return (
    <div>
      <div className="num font-serif text-4xl font-medium tracking-tight text-white">{valor}</div>
      <div className="mt-2 text-base leading-relaxed text-white/75">{texto}</div>
    </div>
  );
}

function Plano({
  nome,
  faixa,
  preco,
  features,
  cta,
  destaque,
}: {
  nome: string;
  faixa: string;
  preco: string;
  features: string[];
  cta: string;
  destaque?: boolean;
}) {
  return (
    <div
      className="card relative flex flex-col p-6"
      style={destaque ? { borderColor: 'var(--accent)', borderWidth: 2 } : undefined}
    >
      {destaque && (
        <span
          className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-xs font-semibold text-white"
          style={{ background: 'var(--accent)' }}
        >
          Mais escolhido
        </span>
      )}
      <div className="text-lg font-semibold text-ink">{nome}</div>
      <div className="mt-1 text-sm text-ink-3">{faixa}</div>
      <div className="num mt-4 text-3xl font-semibold text-ink">
        {preco}
        <span className="text-sm font-normal text-ink-3">/mês</span>
      </div>
      <ul className="mt-5 flex-1 space-y-2.5">
        {features.map((f) => (
          <li key={f} className="flex items-start gap-2 text-base text-ink-2">
            <span className="mt-0.5 text-accent">
              <IconeCheck />
            </span>
            {f}
          </li>
        ))}
      </ul>
      <Link
        href="/entrar?modo=criar"
        className={`btn mt-6 w-full ${destaque ? 'btn-primary' : 'btn-ghost'}`}
        style={{ height: 46 }}
      >
        {cta}
      </Link>
    </div>
  );
}

function Secao({
  id,
  className,
  style,
  children,
}: {
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  children: ReactNode;
}) {
  return (
    <section id={id} className={className} style={style}>
      <div className="mx-auto max-w-6xl px-6">{children}</div>
    </section>
  );
}

export function VendasPage() {
  return (
    <div className="bg-bg text-ink" data-testid="vendas-page">
      {/* ── Nav ── */}
      <header className="sticky top-0 z-30 border-b border-line bg-surface">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <img src="/logo-ama-ink.png" alt="AMA Capital" className="h-8 w-auto" />
          <nav className="hidden items-center gap-8 text-sm font-medium text-ink-2 md:flex">
            {NAV_LINKS.map((l) => (
              <a key={l.href} href={l.href} className="hover:text-ink">
                {l.label}
              </a>
            ))}
          </nav>
          <Link href="/entrar" className="btn btn-primary" style={{ height: 42 }}>
            Começar agora
          </Link>
        </div>
      </header>

      {/* ── Hero ── */}
      <Secao className="py-16 md:py-24">
        <div className="grid items-center gap-12 md:grid-cols-2">
          <div>
            <h1 className="font-serif text-4xl font-medium leading-[1.12] tracking-tight text-ink md:text-5xl">
              Até <span className="italic text-accent">3% de receita a mais</span> por ano. Só evitando
              distrato.
            </h1>
            <p className="mt-5 max-w-lg text-lg leading-relaxed text-ink-2">
              É dinheiro que já está na sua carteira e hoje escapa pela porta. No médio e alto padrão, um a
              cada dez negócios vira distrato, e a maré sobe com os juros e o INCC. Agentes de IA seguram
              cada comprador no momento do risco, do reajuste à entrega das chaves.
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link href="/entrar?modo=criar" className="btn btn-primary" style={{ height: 48 }}>
                Ver quanto posso recuperar <span className="text-base">→</span>
              </Link>
              <a href="#como-funciona" className="btn btn-ghost" style={{ height: 48 }}>
                Como funciona
              </a>
            </div>
            <div className="mt-5 flex items-center gap-2 text-base text-ink-3">
              <span className="text-accent">
                <IconeCheck />
              </span>
              Suba sua base e veja sua exposição ao distrato em minutos.
            </div>
          </div>
          <Foto
            src="/fotos/hero-empreendimento.webp"
            alt="Empreendimento residencial de alto padrão entregue, cercado de área verde"
            className="h-72 md:h-96"
            priority
          />
        </div>
      </Secao>

      {/* ── Extrato / calculadora ── */}
      <Secao className="pb-16">
        <div className="card p-6 md:p-8">
          <div className="text-base font-semibold text-ink-2">O tamanho do que está em jogo</div>
          <div className="mt-1 text-sm text-ink-3">
            Exemplo: 300 contratos · ticket médio R$ 500 mil · distrato a 10,5%
          </div>
          <div className="mt-6 flex flex-col items-center gap-6 sm:flex-row sm:justify-center sm:gap-10">
            <div className="text-center sm:text-left">
              <div className="num font-serif text-4xl font-medium tracking-tight text-accent">até 3%</div>
              <div className="mt-1.5 max-w-[220px] text-sm leading-relaxed text-ink-3">
                de receita a mais por ano, recuperada da carteira que você já vendeu.
              </div>
            </div>
            <span className="hidden text-2xl text-ink-4 sm:block">→</span>
            <div className="text-center sm:text-left">
              <div className="num font-serif text-4xl font-medium tracking-tight text-ink">R$ 4,6 mi</div>
              <div className="mt-1.5 max-w-[260px] text-sm leading-relaxed text-ink-3">
                a mais no ano sobre uma carteira de R$ 150 milhões, só segurando parte dos distratos.
              </div>
            </div>
          </div>
          <div className="mt-6 border-t border-line pt-4 text-xs leading-relaxed text-ink-4">
            Fonte: relação distrato sobre venda no segmento Médio e Alto Padrão, Indicadores ABRAINC-FIPE
            (2024). A taxa vem subindo com a alta dos juros e do INCC.
          </div>
        </div>
      </Secao>

      {/* ── O que muda: 3 pilares ── */}
      <Secao id="o-que-muda" className="py-16 md:py-20">
        <h2 className="max-w-2xl font-serif text-3xl font-medium tracking-tight text-ink">
          Você já fez o trabalho difícil: vendeu. O ganho está em não deixar a venda escapar.
        </h2>
        <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-ink-2">
          No médio e alto padrão, um a cada dez negócios vendidos termina em distrato, e a maré está
          subindo de novo. Cada contrato que chega saudável até a entrega é margem que você já conquistou.
          São três frentes onde esse dinheiro escorre, e onde o agente o segura.
        </p>
        <div className="mt-12 space-y-14">
          <Pilar
            n={1}
            titulo="O comprador realiza o sonho em vez de desistir dele"
            texto="Quando o saldo é corrigido pelo INCC e ninguém preparou o comprador, ele se assusta e pede o distrato. O agente antecipa esse momento com uma simulação clara, e a família segue rumo às chaves."
            selo="Um distrato evitado paga 2 anos de plataforma"
            foto={{ src: '/fotos/comprador-chaves.webp', alt: 'Casal sorrindo com as chaves do novo apartamento' }}
          />
          <Pilar
            n={2}
            titulo="A parcela entra no prazo, e o caixa continua girando"
            texto="A inadimplência começa pequena e silenciosa. No primeiro sinal de atraso, o agente abre uma renegociação estruturada, e o recebível volta a entrar em vez de virar prejuízo, em toda a carteira."
            selo="Recebível que volta em vez de virar prejuízo"
            foto={{ src: '/fotos/obra-crescimento.webp', alt: 'Obra de incorporação ao entardecer com guindaste' }}
          />
          <Pilar
            n={3}
            titulo="Você cresce a carteira sem inflar a folha"
            texto="Acompanhar contrato por contrato custa uma equipe inteira, e ainda assim casos passam. O agente cobre 100% da carteira por uma fração do custo. Quando você lança o próximo empreendimento, não precisa contratar mais."
            selo="Cresça a carteira sem crescer a folha"
            foto={{ src: '/fotos/gestao-carteira.webp', alt: 'Engenheiro e gestor acompanhando a obra com prancha e tablet' }}
          />
        </div>
      </Secao>

      {/* ── Como funciona ── */}
      <Secao id="como-funciona" className="bg-bg-warm py-16 md:py-20">
        <h2 className="max-w-xl font-serif text-3xl font-medium tracking-tight text-ink">
          Você sobe a base uma vez. Os agentes cuidam do resto, todo dia.
        </h2>
        <p className="mt-4 max-w-xl text-[17px] leading-relaxed text-ink-2">
          Sem implantar sistema, sem treinar equipe. Em minutos, cada contrato da sua carteira passa a ser
          acompanhado.
        </p>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          <Passo
            n={1}
            titulo="Suba sua base de contratos"
            texto="Planilha, CSV ou conexão com seu ERP. O agente lê tudo, mapeia as colunas sozinho e organiza a carteira, mesmo que o arquivo esteja bagunçado."
          />
          <Passo
            n={2}
            titulo="Os agentes acompanham o risco"
            texto="Cada contrato é avaliado diariamente: reajuste chegando, parcela atrasando, padrão piorando, marco de obra sensível. A prioridade se organiza sozinha."
          />
          <Passo
            n={3}
            titulo="A conversa certa, na hora certa"
            texto="O agente fala com o comprador antes do problema: simulação antes do reajuste, renegociação no atraso. Você aprova cada mensagem, ou deixa no automático quando confiar."
          />
        </div>
      </Secao>

      {/* ── Números grandes ── */}
      <section className="px-6 py-14" style={{ background: 'var(--brand-gradient)' }}>
        <div className="mx-auto grid max-w-5xl gap-8 text-center sm:grid-cols-3">
          <StatBlock valor="10,5%" texto="das vendas no médio e alto padrão viram distrato, e subindo" />
          <StatBlock valor="R$ 40 mil" texto="é o custo médio de um único distrato para a incorporadora" />
          <StatBlock valor="100%" texto="dos contratos acompanhados, todos os dias, sem exceção" />
        </div>
      </section>

      {/* ── Dados protegidos ── */}
      <Secao className="py-16 md:py-20">
        <h2 className="max-w-xl font-serif text-3xl font-medium tracking-tight text-ink">
          Seus dados protegidos. Sua comunicação sob controle.
        </h2>
        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          <div className="card p-6">
            <IconeCadeado />
            <h3 className="mt-4 text-[17px] font-semibold text-ink">Seus dados, isolados e protegidos</h3>
            <p className="mt-2 text-base leading-relaxed text-ink-3">
              Cada incorporadora tem um ambiente próprio e fechado. Ninguém vê os seus contratos além de
              você. Conformidade total com a LGPD.
            </p>
          </div>
          <div className="card p-6">
            <IconeMensagem />
            <h3 className="mt-4 text-[17px] font-semibold text-ink">Você no controle de cada mensagem</h3>
            <p className="mt-2 text-base leading-relaxed text-ink-3">
              No início, nada é enviado ao comprador sem a sua aprovação. Você decide quando soltar o
              automático e mantém o histórico de tudo.
            </p>
          </div>
        </div>
      </Secao>

      {/* ── Preços ── */}
      <Secao id="precos" className="bg-bg-warm py-16 md:py-20">
        <h2 className="max-w-xl font-serif text-3xl font-medium tracking-tight text-ink">
          Um único distrato evitado paga mais de dois anos.
        </h2>
        <p className="mt-4 max-w-xl text-[17px] leading-relaxed text-ink-2">
          Você cobra por contratos monitorados. Sem fidelidade. Comece e cancele quando quiser.
        </p>
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          <Plano
            nome="Starter"
            faixa="Até 200 contratos ativos"
            preco="R$ 1.497"
            features={[
              'Acompanhamento contínuo de risco',
              'Comunicação agêntica',
              'Painel de resultado',
              'Suporte por e-mail',
            ]}
            cta="Começar com Starter"
          />
          <Plano
            nome="Growth"
            faixa="200 a 600 contratos ativos"
            preco="R$ 3.000"
            features={[
              'Tudo do Starter',
              'Comparativo tratado vs. controle',
              'Integração com ERP',
              'Suporte prioritário',
            ]}
            cta="Começar com Growth"
            destaque
          />
          <Plano
            nome="Scale"
            faixa="600 a 1.200 contratos ativos"
            preco="R$ 4.997"
            features={[
              'Tudo do Growth',
              'Múltiplos empreendimentos',
              'Gerente de conta dedicado',
              'Onboarding assistido',
            ]}
            cta="Começar com Scale"
          />
        </div>
      </Secao>

      {/* ── CTA final ── */}
      <section className="px-6 py-16 text-center" style={{ background: 'var(--brand-gradient)' }}>
        <div className="mx-auto max-w-2xl">
          <h2 className="font-serif text-3xl font-medium tracking-tight text-white md:text-4xl">
            O próximo distrato já está na sua carteira. Dá tempo de evitá-lo.
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-white/80">
            Suba sua primeira base em minutos e veja, ainda hoje, quais contratos estão no momento de
            risco.
          </p>
          <Link
            href="/entrar?modo=criar"
            className="btn mt-7 inline-flex"
            style={{ height: 48, background: '#fff', color: 'var(--accent-deep)' }}
          >
            Criar conta e subir minha base <span className="text-base">→</span>
          </Link>
        </div>
      </section>

      {/* ── Faixa institucional ── */}
      <section
        className="px-6 py-10 text-center sm:py-14"
        style={{ background: '#14171C' }}
        data-testid="faixa-institucional"
      >
        <div className="mx-auto max-w-2xl">
          <div className="text-sm font-semibold text-white">AMA Capital</div>
          <p className="mt-3 text-[17px] leading-relaxed text-white/75 sm:text-lg" data-testid="faixa-institucional-texto">
            Cortamos custos e automatizamos processos com agentes de IA. Este é o nosso trabalho para
            incorporadoras. O mesmo motor se aplica a qualquer operação que perca dinheiro por processo
            lento ou manual.
          </p>
        </div>
      </section>

      {/* ── Rodapé ── */}
      <footer className="border-t border-line px-6 py-8 text-center" data-testid="vendas-rodape">
        <img src="/logo-ama-ink.png" alt="AMA Capital" className="mx-auto h-6 w-auto opacity-70" />
        <div className="mt-3 text-xs text-ink-4">Retenção pós-venda para incorporadoras · © 2026</div>
      </footer>
    </div>
  );
}
